<template>
  <WithLoader :isLoading="!identityLoaded || isLoading" classType="primary">
    <div
      class="campaigns-table__wrapper"
      :class="{ [extraClass]: !!extraClass }"
    >
      <VDataTable
        extraClass="campaigns-table"
        :extraClass="
          `campaigns-table ${
            getFilteredDeletedCampaigns.length > menuSwitchingCount
              ? 'large'
              : ''
          }`
        "
        classType="primary"
        :headers="tableFields.headers"
        :rows="tableFields.rows"
        :currentSort="{ ...sort, value: !!sort.order }"
        showEmptyHeading="true"
        @sort="onSort"
      >
      </VDataTable>
    </div>

    <VConfirmModal
      :isOpened="deleteCampaignConfirmOpened"
      @approve="performDeleteCampaign"
      @close="cancelDeleteCampaign"
      @cancel="cancelDeleteCampaign"
    >
      <div class="confirm-message">
        Deleting the
        <span>
          {{ campaignToDelete.name ? campaignToDelete.name : "Noname" }}
        </span>
        campaign?
      </div>
    </VConfirmModal>
  </WithLoader>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import { mapActions, mapGetters } from "vuex";
import get from "lodash/get";
import mapKeys from "lodash/mapKeys";

// components
import VDataTable from "@/components/VDataTable";
import WithLoader from "@/components/WithLoader";
import VConfirmModal from "@/components/VConfirmModal";
import VContextMenu from "@/components/VContextMenu";
import VTextTooltiped from "@/components/VTextTooltiped";
import VTagsExpandableList from "@/components/VTagsExpandableList";
import VPreviewImage from "@/components/VPreviewImage";
import VEditableText from "@/components/VEditableText";
import VDateTooltiped from "@/components/VDateTooltiped";
import VSwitcher from "@/components/VSwitcher";

// mixins
import withNavigate from "@/mixins/withNavigate";
import withToasted from "@/mixins/withToasted";
import withCopy from "@/mixins/withCopy";

import {
  getFormattedDate,
  getViewTemplateFormat,
  getPostcardFullType,
  getLetterType,
  formatMomentDT,
  convertTagsToSelectListFormat,
  capitalizeFirstLetter
} from "@/utils/util";

import { TABLE_SWITCHING_CONTEXT_MENU_MIN_COUNT as menuSwitchingCount } from "@/constants/common";
import { getTableContextMenu } from "@/utils/table";
import { convertDataToApi } from "@/utils/util";

export default Vue.component("CampaignsTable", {
  props: {
    extraClass: VueTypes.string.def("")
  },

  mixins: [withNavigate, withToasted, withCopy],

  components: {
    VDataTable,
    WithLoader,
    VConfirmModal
  },

  data() {
    return {
      deleteCampaignConfirmOpened: false,
      campaignToDelete: {},
      expandedIds: [],
      menuSwitchingCount,
      renameActiveId: null,
      tagsPerPage: []
    };
  },

  methods: {
    _navigateTemplate(campaign) {
      const navigateName =
        campaign.type === "letter"
          ? "letters-templates-settings"
          : "postcards-templates-settings";
      if (campaign.template.deleted_at) {
        let templateName = campaign.template.name;
        this.toast(`Template "${templateName}" doesn't exist!`, "error");
      } else if (this.hasTemplate(campaign)) {
        this.navigate({
          name: navigateName,
          params: { id: this.getTemplate(campaign).id }
        });
      }
    },

    _onRenameHandler(id) {
      this.renameActiveId = id;
    },

    _changeExpandIds(id) {
      const elIndex = this.expandedIds.indexOf(id);
      return ~elIndex
        ? this.expandedIds.splice(elIndex, 1)
        : this.expandedIds.push(id);
    },

    _onDuplicateHandler(id) {
      const campaign = this.getCampaign(id);
      const { deleted_at } = campaign.template || {};
      const {
        name,
        fromAddress,
        tags,
        template_id,
        type,
        use_account_address
      } = campaign;
      const addressForApi = convertDataToApi("addressOld", fromAddress);
      const { address_line_1, address_line_2, ...rest } = addressForApi;

      const data = {
        tags,
        ...rest,
        template_id,
        type,
        deleted_at,
        name: `${name} - Copy`,
        address_street: address_line_1,
        address_unit: address_line_2,
        use_account_address
      };

      this.createCampaign({
        data,
        onSuccess: () => {
          this.fetchCampaigns({
            page: 1,
            search: this.search,
            page_size: this.itemsPerPage
          });
          this.toast(`Campaign ${name} has been duplicated!`);
        },
        onError: e => {
          const m =
            e.description && e.description.name
              ? "Name must be less than 40 characters."
              : "Something went wrong with duplicating campaign";
          this.toast(m, "error");
        }
      });
    },

    _updateCampaign(id, key, type, val) {
      const self = this;

      if (!val) {
        this.toast(
          "Campaign name saving failed. Field can't be empty.",
          "error"
        );
      } else {
        this.editCampaign({
          id,
          data: { [key]: val, type },
          onSuccess: () => {
            this.toast("Campaign name has been changed!");
            self.renameActiveId = null;
          }
        });
      }
    },

    _getTags(campaign) {
      const tags = this.getTags(campaign);

      if (!tags.length || tags.includes(undefined)) {
        return [];
      }

      return tags.map(tag => {
        if (tag && tag.name) {
          return {
            value: tag.id,
            key: tag.id,
            label: tag.name
          };
        }
      });
    },

    isCampaignMailed(campaign) {
      return !!campaign.mailed;
    },

    // getPostcardType(type, mail_type) {
    //   return getPostcardFullType(type);
    // },

    getTemplate(campaign) {
      const template = get(campaign, "template");
      return getViewTemplateFormat(template, campaign.type);
    },

    hasTemplate(campaign) {
      return !!get(campaign, "template");
    },

    getTemplateType(campaign) {
      if (campaign.type === "postcard") {
        if (get(campaign, "template.deleted_at")) {
          return "Template removed";
        }

        return `${getPostcardFullType(
          get(campaign, "template.type"),
          get(campaign, "template.mail_type")
        )}`;
      }
      return getLetterType(
        get(campaign, "template.color"),
        get(campaign, "template.mail_type")
      );
    },

    getTemplateId(campaign) {
      return get(campaign, "templateId");
    },

    formatDate(date) {
      return getFormattedDate(date);
    },

    getTags(campaign) {
      return campaign.tags.map(tag => this.getTag(tag));
    },

    onSort(sorting) {
      this.fetchCampaigns({
        page: this.currentPage,
        search: this.search,
        sort: sorting
      });

      this.changeSort(sorting);
    },

    handleDeleteCampaign(e, campaignId, name) {
      e.stopPropagation();
      this.deleteCampaignConfirmOpened = true;
      this.campaignToDelete = {
        id: campaignId,
        name
      };
    },

    cancelDeleteCampaign() {
      this.deleteCampaignConfirmOpened = false;
      this.campaignToDelete = {};
    },

    performDeleteCampaign() {
      if (this.campaignToDelete.id) {
        this.deleteCampaign({
          id: this.campaignToDelete.id,
          callback: () => {
            this.toast("Campaign has been deleted successfully");
          }
        });
      } else {
        this.toast("Unable to delete campaign!", "error");
      }

      this.deleteCampaignConfirmOpened = false;
    },

    ...mapActions("campaigns", [
      "fetchCampaigns",
      "deleteCampaign",
      "changeSort",
      "editCampaign",
      "createCampaign"
    ]),

    ...mapActions("tags", ["fetchTagsPerPageList"])
  },

  computed: {
    isLoading() {
      return this.$store.state.campaigns.isLoading;
    },

    identityLoaded() {
      return this.$store.state.account.identityLoaded;
    },

    tableFields() {
      return {
        headers: [
          {
            key: "name",
            label: "Template/Name",
            sortable: true,
            sortType: "string",
            width: "22%"
          },
          {
            key: "type",
            label: "Type",
            sortable: true,
            width: "15%"
          },
          {
            key: "created_at",
            label: "Created",
            sortable: true,
            width: "15%"
          },
          {
            key: "updated_at",
            label: "Modified",
            sortable: true,
            width: "15%"
          },
          {
            key: "mailed_at",
            label: "State",
            sortable: true,
            width: "15%"
          },
          {
            key: "tags",
            label: "Tags",
            width: "15%",
            extraClass: "tags"
          },
          {
            key: "action",
            label: "",
            extraClass: "campaigns-actions",
            width: "3%"
          }
        ],

        rows: this.getFilteredDeletedCampaigns.map(campaign => ({
          key: campaign.id,
          click: () => {
            this.navigate({
              name: "campaign-details",
              params: { id: campaign.id }
            });
          },
          values: [
            {
              key: "preview",
              component: VPreviewImage,
              props: {
                image: this.hasTemplate(campaign)
                  ? this.getTemplate(campaign).preview
                  : null,
                title: this.hasTemplate(campaign)
                  ? this.getTemplate(campaign).name
                  : "No Template",
                onClickHandler: () => this._navigateTemplate(campaign),
                orientation:
                  campaign.type === "letter" ? "vertical" : "horizontal",
                extraClass: "in-campaign-table",

                // text component
                component: VEditableText,
                textComponentProps: {
                  _key: "name",
                  handleChange: (key, val) =>
                    this._updateCampaign(campaign.id, key, campaign.type, val),
                  value: campaign.name,
                  hideLabel: true,
                  hideIcon: true,
                  extraClass: "campaign-name",
                  activateOnClick: false,
                  inputActive: this.renameActiveId === campaign.id,
                  blurHandler: () => (this.renameActiveId = null)
                }
              },
              extraClass: "campaign-preview"
            },
            {
              key: "size",
              value: this.hasTemplate(campaign)
                ? this.getTemplateType(campaign)
                : "No Type"
            },
            {
              key: "created_at",
              component: VDateTooltiped,
              props: {
                date: campaign.created
              }
            },
            {
              key: "updated_at",
              component: VDateTooltiped,
              props: {
                date: campaign.updated
              }
            },
            {
              key: "mailed",
              component: this.isCampaignMailed(campaign)
                ? VTextTooltiped
                : null,
              value: this.isCampaignMailed(campaign) ? null : "Draft",
              props: {
                tooltipText: this.isCampaignMailed(campaign)
                  ? formatMomentDT(campaign.mailed)
                  : null,
                text: campaign.mailed ? "Finished" : "Draft",
                noPadding: false
              },
              extraClass: "templates-status"
            },

            {
              key: "tags",
              component: VTagsExpandableList,
              props: {
                tags: campaign.tags.reduce((arr, tag) => {
                  if (this.tagsPerPage[tag]) {
                    arr.push(this.tagsPerPage[tag]);
                  }
                  return arr;
                }, []),
                onExpand: id => this._changeExpandIds(id),
                identifier: campaign.id,
                onceAtTime: true
                // expanded: this.expandedId === campaign.id
              },
              extraClass: "tags"
            },
            getTableContextMenu({
              rename: this.isCampaignMailed(campaign)
                ? null
                : (e, a) => this._onRenameHandler(campaign.id),
              copy: (e, a) => this.copy(campaign.id),
              duplicate: (e, a) => this._onDuplicateHandler(campaign.id),
              remove: e =>
                this.handleDeleteCampaign(e, campaign.id, campaign.name)
            })
          ],
          extraClass: this.expandedIds.includes(campaign.id)
            ? "tags-expand"
            : ""
        }))
      };
    },

    ...mapGetters("campaigns", [
      "getFilteredDeletedCampaigns",
      "sort",
      "pagination",
      "search",
      "getCampaign"
    ]),

    // ...mapGetters("templates", ["getTemplate"]),
    ...mapGetters("contacts", ["getTag"]),
    ...mapGetters("service", ["itemsPerPage"])
  },

  watch: {
    getFilteredDeletedCampaigns(val) {
      if (val) {
        let uniqueTags = [
          ...new Set(val.map(campaign => campaign.tags).flat())
        ];
        let uniqueTagsString = `&id=${[...uniqueTags].join(",")}`;
        this.fetchTagsPerPageList({
          queryString: uniqueTagsString,
          onSuccess: resp => {
            let convertedTags = convertTagsToSelectListFormat(resp);
            this.tagsPerPage = mapKeys(convertedTags, "value");
          },
          onError: () => console.log("error")
        });
      }
    }
  },

  created() {
    if (!this.isLoading) {
      this.fetchCampaigns({
        page: 1,
        sort: this.sort,
        search: this.search,
        page_size: this.itemsPerPage
      });
    }
  },

  destroyed() {
    this.changeSort({ key: "updated_at", value: false });
  }
});
</script>

<style lang="scss">
table.campaigns-table {
  table-layout: fixed;
  tr {
    @include quantity-query($menu-switching-last-items-count, less) {
      td.campaigns-link-menu {
        .menu-wrapper {
          bottom: 35px;
          top: auto;
        }
      }
    }

    td.tags {
      padding: 5px 10px;

      /deep/ .expand {
        margin-top: 0;
      }
    }

    th.tags {
      span {
        padding-left: 20px;
      }
    }

    &.tags-expand {
      td {
        padding-top: 21px;
        vertical-align: top;

        &.campaign-preview,
        &.tags {
          padding-top: 5px;
        }

        &.context-menu {
          padding-top: 0;
        }
      }
    }
  }
}

div.v-editable-text__wrapper.campaign-name {
  margin-bottom: 0;
  /*width: 170px;*/
}

.campaigns-table__no-data {
  display: flex;
  justify-content: center;
}

.no-campaigns {
  display: inline-block;
  margin-top: 30px;
  font-family: Lato-Bold, Arial, sans-serif;
  font-size: 26px;
  line-height: 31px;
  text-align: center;
  text-transform: capitalize;
  color: $empty-grey;
}

/* for vpreviewimage */
.in-campaign-table.vertical {
  .image-wrapper {
    margin: 0 15px;
  }
}
</style>
